import React, { useState, useEffect } from 'react';
// Components
import FieldsToSelectDropdown from './components/FieldsToSelectDropdown';
import Checkbox from 'shared/inputs/BaseCheckbox';
import DatePicker from 'shared/inputs/datePickers/BaseDatePicker';
// Helpers
import { has, isNil } from 'lodash';
import { format, addDays } from 'date-fns';
import { usePrevious } from '@reactuses/core';

const numericRegex = /^(-?\d+)$/;
const DELTA_PREFIX = 'DateTimeDelta';
const BETWEEN_OPERATOR = 'between';

const ISNULL_OPERATOR = 'isnull';
const ISNOTNULL_OPERATOR = 'isnotnull';

const toDisplayValue = (value) => {
  if (typeof value !== 'string') {
    return value;
  }
  if (value.startsWith(DELTA_PREFIX)) {
    return value.replace(DELTA_PREFIX, '');
  }
  return new Date(value);
};

const convertDeltaDateTimeValueToDate = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  if (value.substring(0, DELTA_PREFIX.length) === DELTA_PREFIX) {
    const numberOfDays = parseInt(value.substring(DELTA_PREFIX.length));
    return addDays(new Date(), numberOfDays);
  }

  return value;
};

const DateFilter = ({
  filter, fieldsNameForSearch = [], onFilterChange, fieldInfo,
}) => {
  const { value, operator } = filter;
  const previousOperator = usePrevious(operator);

  const getInitialFromDate = () => (isNil(value?.start) ? value?.start : toDisplayValue(value?.start));
  const getInitialToDate = () => (isNil(value?.end) ? value?.end : toDisplayValue(value?.end));

  const [compareWithOtherFieldDisplayValue, setCompareWithOtherFieldDisplayValue] = useState(
    filter.compareWithOtherField ? value : null,
  );
  const [compareWithOtherField, setCompareWithOtherField] = useState(filter.compareWithOtherField);

  // Date picker (one value)
  const [displayValue, setDisplayValue] = useState(!filter.compareWithOtherField ? toDisplayValue(value) : null);

  const unaryOperatorSelected = operator === ISNULL_OPERATOR || operator === ISNOTNULL_OPERATOR;

  const onChange = ({ value }) => {
    const isNumeric = (typeof value === 'string' && numericRegex.test(value)) || typeof value === 'number';
    // eslint-disable-next-line no-nested-ternary
    const formatted = value
      ? isNumeric ? Number(value) : format(value, 'YYYY-MM-DD HH:mm:ss')
      : null;
    onFilterChange.call(undefined, {
      nextFilter: {
        ...filter,
        value: isNumeric ? `${DELTA_PREFIX}${formatted}` : formatted,
        fieldInfo,
      },
    });
    setDisplayValue(toDisplayValue(isNumeric ? formatted : value));
  };

  useEffect(() => {
    if (!has(filter, 'fieldInfo')) {
      onFilterChange.call(undefined, {
        nextFilter: {
          ...filter,
          fieldInfo,
        },
      });
    }
  }, []);

  // From/To dates values
  const [fromDate, setFromDate] = useState(getInitialFromDate());
  const [toDate, setToDate] = useState(getInitialToDate());

  useEffect(() => {
    if (previousOperator == BETWEEN_OPERATOR && has(value, 'start')) {
      onFilterChange.call(undefined, {
        nextFilter: {
          ...filter,
          value: convertDeltaDateTimeValueToDate(value.start),
          fieldInfo,
        },
      });
    }
  }, [operator]);

  // Set initial values when value is passed
  useEffect(() => {
    if ((!isNil(displayValue) && !has(displayValue, 'start')) || isNil(value)) {
      return;
    }
    // If between value
    if (has(value, 'start') && operator === BETWEEN_OPERATOR) {
      setFromDate(getInitialFromDate());
      setToDate(getInitialToDate());
    } else {
      // If one value
      setDisplayValue(!filter.compareWithOtherField ? toDisplayValue(value) : null);
    }
  }, [value]);

  const handleFromDateChange = ({ value: newValue }) => {
    const isNumeric = (typeof newValue === 'string' && numericRegex.test(newValue)) || typeof newValue === 'number';
    const formatted = newValue && isNumeric ? Number(newValue) : newValue;

    onFilterChange.call(undefined, {
      nextFilter: {
        ...filter,
        value: {
          ...value,
          start: isNumeric ? `${DELTA_PREFIX}${formatted}` : formatted,
        },
        fieldInfo,
      },
    });

    setFromDate(toDisplayValue(isNumeric ? formatted : newValue));
  };

  const handleToDateChange = ({ value: newValue }) => {
    const isNumeric = (typeof newValue === 'string' && numericRegex.test(newValue)) || typeof newValue === 'number';
    const formatted = newValue && isNumeric ? Number(newValue) : newValue;

    onFilterChange.call(undefined, {
      nextFilter: {
        ...filter,
        value: {
          ...value,
          end: isNumeric ? `${DELTA_PREFIX}${formatted}` : formatted,
        },
        fieldInfo,
      },
    });

    setToDate(toDisplayValue(isNumeric ? formatted : newValue));
  };

  const showValueInput = () => !unaryOperatorSelected;

  // Field to compare
  useEffect(() => {
    setCompareWithOtherField(filter.compareWithOtherField);
  }, [filter.compareWithOtherField]);

  useEffect(() => {
    if (filter.compareWithOtherField) {
      setCompareWithOtherFieldDisplayValue(value);
    }
  }, [value]);

  const handleFieldToCompareChanged = (event) => {
    const value = event.target.value.field;

    const newFilter = {
      ...filter,
      value,
      compareWithOtherField: true,
      fieldInfo,
    };

    onFilterChange.call(undefined, {
      nextFilter: newFilter,
    });

    setCompareWithOtherFieldDisplayValue(event.target.value);
  };

  // Render Date picker(s)
  const renderValueComponent = () => {
    if (operator === 'between') {
      return (
        <div style={{ display: 'flex' }}>
          <div style={{ marginTop: '5px' }}>
            From
          </div>
          <div style={{ marginLeft: '5px' }}>
            <DatePicker allowNumeric minimal onChange={handleFromDateChange} value={fromDate} />
          </div>
          <div style={{ marginLeft: '5px', marginTop: '5px' }}>
            To
          </div>
          <div style={{ marginLeft: '5px' }}>
            <DatePicker allowNumeric minimal onChange={handleToDateChange} value={toDate} />
          </div>
        </div>
      );
    }

    return <DatePicker allowNumeric minimal onChange={onChange} value={displayValue} />;
  };

  const handleCompareWithOtherFields = ({ value }) => {
    const newFilter = value ? {
      ...filter,
      value: typeof compareWithOtherFieldDisplayValue === 'object'
        ? compareWithOtherFieldDisplayValue?.field : compareWithOtherFieldDisplayValue,
      compareWithOtherField: true,
    } : {
      field: filter.field,
      operator: filter.operator,
      value: typeof displayValue === 'object' ? displayValue?.value : displayValue,
    };

    onFilterChange.call(undefined, {
      nextFilter: newFilter,
    });

    const setValue = !value ? setCompareWithOtherFieldDisplayValue : setDisplayValue;
    setValue(null);
    setCompareWithOtherField(value);
  };

  return (
    <>
      {showValueInput() ? (
        <div className='app-filters-input'>
          {compareWithOtherField && fieldsNameForSearch.length > 0
            ? (
              <FieldsToSelectDropdown
                fields={fieldsNameForSearch}
                onFieldChanged={handleFieldToCompareChanged}
                value={compareWithOtherFieldDisplayValue}
              />
            ) : (
              filter.operator !== 'isnull' && filter.operator !== 'isnotnull'
            ) && renderValueComponent()}
          {fieldsNameForSearch.length > 0 && (
            <div className='filter-cbx-wrapper'>
              <Checkbox
                value={compareWithOtherField}
                label='Compare with other field'
                onChange={handleCompareWithOtherFields}
              />
            </div>
          )}
        </div>
      ) : <div />}
    </>
  );
};

export default DateFilter;